import { Table } from 'Components/organisms';
import { useMemo, useState } from 'react';
import {
    AmpSettingDto,
    FilterCriteria,
    JamfComplianceBenchmarksSetting,
    JAMF_COMPLIANCE_BENCHMARKS_SETTINGS,
    JamfComplianceBenchmarkDto,
} from '@ampsec/platform-client';
import { COMPLIANCE_BENCHMARKS_CONFIG_COLUMNS } from './ComplainceBenchmarksConfigColumns';
import { getJamfConnectorSetting, updateSettings } from 'Apis/library';
import { SEARCH_PLACEHOLDER } from 'Core-utils/constants/constants';
import { useApiObjectData } from 'Hooks/useApiObjectData';
import './styles.scss';
import { Typography } from 'Components/atoms';
import { useI18n } from 'Hooks/useI18n';
import { SearchBar } from 'Components/molecules';
import { Status } from 'Core-utils/helpers/helpers';
import ComplianceBenchmarkConfigCredentials from './ComplianceBenchmarkConfigCredentials';

interface ComplianceBenchmarksConfigTableProps {
    cid: string;
    filter?: FilterCriteria;
}

interface subHeaderContentProps {
    searchInputValue: string;
    onSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const SubHeaderContent = ({ searchInputValue, onSearchChange }: subHeaderContentProps) => {
    return (
        <div className="d-flex justify-content-between w-100 p-0">
            <SearchBar
                value={searchInputValue}
                onChange={onSearchChange}
                placeholder={SEARCH_PLACEHOLDER}
                searchClassName="search-container__input-cont"
            />
        </div>
    );
};

const ROW_PER_PAGE_OPTIONS = [10, 20, 30, 40];
const ComplianceBenchmarksConfigTable = ({ cid }: ComplianceBenchmarksConfigTableProps) => {
    const [searchValue, setSearchValue] = useState<string>('');
    const { data: jamfConnectorSettings, isLoading, refresh } = useApiObjectData<
        { [key: string]: AmpSettingDto },
        { [key: string]: AmpSettingDto }
    >(
        'JAMF_CONNECTOR_SETTINGS',
        () => getJamfConnectorSetting(cid),
        (a) => a,
    );

    const complianceBenchmarksData: JamfComplianceBenchmarksSetting = (jamfConnectorSettings?.[
        JAMF_COMPLIANCE_BENCHMARKS_SETTINGS
    ]?.value as unknown) as JamfComplianceBenchmarksSetting;
    const TABLE_BODY_HEIGHT = '25rem';
    const { t } = useI18n();
    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = event.target.value;
        setSearchValue(inputValue);
    };

    const benchmarksData = useMemo(() => {
        const benchmarks = complianceBenchmarksData?.benchmarks ?? [];
        if (!benchmarks.length) {
            return [];
        }
        const normalizedSearch = searchValue.trim().toLowerCase();
        return benchmarks.filter((item) => item.title.toLowerCase().includes(normalizedSearch));
    }, [complianceBenchmarksData?.benchmarks, searchValue]);

    const handleUpdateStatus = (row: JamfComplianceBenchmarkDto, status: Status) => {
        updateSetting({ ...row, status: status });
    };

    const updateSetting = (value: unknown) => {
        const existingSetting = jamfConnectorSettings?.[JAMF_COMPLIANCE_BENCHMARKS_SETTINGS] as AmpSettingDto;
        if (existingSetting) {
            updateSettings(existingSetting.id, {
                id: existingSetting.id,
                key: JAMF_COMPLIANCE_BENCHMARKS_SETTINGS,
                value: value,
            } as AmpSettingDto).then(() => refresh());
        }
    };

    let table = (
        <Table
            cacheKey={'complianceBenchmarks'}
            data={benchmarksData ?? []}
            dataIsLoading={isLoading}
            columns={COMPLIANCE_BENCHMARKS_CONFIG_COLUMNS(handleUpdateStatus)}
            rowsPerPageOptions={ROW_PER_PAGE_OPTIONS}
            transformer={(a) => a}
            subHeaderVariant="header-with-refresh-button"
            subHeaderContent={<SubHeaderContent searchInputValue={searchValue} onSearchChange={handleSearchChange} />}
            tableBodyHeight={TABLE_BODY_HEIGHT}
            pagination={false}
            defaultSortId="status"
        />
    );
    if (!jamfConnectorSettings?.[JAMF_COMPLIANCE_BENCHMARKS_SETTINGS]) {
        table = (
            <Typography variant="subtitle3" className="text-text-high-emphasis">
                {t('NO_JAMF_COMPLIANCE_BENCHMARKS_SETTINGS')}
            </Typography>
        );
    }
    return (
        <div className="compliance-benchmark">
            <ComplianceBenchmarkConfigCredentials cid={cid} />
            {table}
        </div>
    );
};

export default ComplianceBenchmarksConfigTable;
